export class MarqueeElement extends HTMLElement {
  private animationId: number | null = null
  private scrollPosition: number = 0
  private content: HTMLElement | null = null
  private autoplaySpeed: number = 3000
  private isPaused: boolean = false
  private contentWidth: number = 0
  private resizeObserver: ResizeObserver | null = null
  private originalChildren: Node[] = []

  connectedCallback() {
    this.setupMarquee()
    this.startAnimation()
  }

  disconnectedCallback() {
    this.stopAnimation()
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }

  private setupMarquee() {
    // Get autoplay speed from data attribute
    const speedAttr = this.getAttribute('data-autoplay-speed')
    if (speedAttr) {
      this.autoplaySpeed = parseInt(speedAttr, 10) || 3000
    }

    // Store original children
    this.originalChildren = Array.from(this.childNodes)

    // Create content wrapper that will hold original + cloned content
    this.content = document.createElement('div')
    this.content.style.cssText = `
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
      will-change: transform;
      min-width: max-content;
    `

    // Move original children to content wrapper
    this.originalChildren.forEach(child => {
      this.content!.appendChild(child)
    })

    // Clone the content for seamless loop
    this.originalChildren.forEach(child => {
      const clone = child.cloneNode(true)
      this.content!.appendChild(clone)
    })

    // Add content to element
    this.appendChild(this.content)

    // Setup resize observer to recalculate content width when content changes
    this.setupResizeObserver()

    // Calculate initial content width after a brief delay to ensure DOM is rendered
    setTimeout(() => this.calculateContentWidth(), 0)

    // Pause on hover
    this.addEventListener('mouseenter', () => this.pause())
    this.addEventListener('mouseleave', () => this.resume())
  }

  private setupResizeObserver() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.calculateContentWidth()
      })

      if (this.content) {
        this.resizeObserver.observe(this.content)
      }
    }
  }

  private calculateContentWidth() {
    if (this.content && this.originalChildren.length > 0) {
      // Calculate width based on original children only (half the total content)
      this.contentWidth = this.content.scrollWidth / 2
    }
  }

  private startAnimation() {
    if (this.animationId) return

    const animate = () => {
      if (this.isPaused) {
        this.animationId = requestAnimationFrame(animate)
        return
      }

      if (this.content && this.contentWidth > 0) {
        // Calculate speed based on autoplay speed (lower number = faster)
        const speedMultiplier = Math.max(0.2, 3000 / this.autoplaySpeed)
        this.scrollPosition += speedMultiplier
        this.content.style.transform = `translateX(-${this.scrollPosition}px)`

        // Reset position when original content is fully scrolled out
        if (this.scrollPosition >= this.contentWidth) {
          this.scrollPosition = 0
        }
      }

      this.animationId = requestAnimationFrame(animate)
    }

    this.animationId = requestAnimationFrame(animate)
  }

  private stopAnimation() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
  }

  private pause() {
    this.isPaused = true
  }

  private resume() {
    this.isPaused = false
  }

  // Public methods for external control
  public pauseMarquee() {
    this.pause()
  }

  public resumeMarquee() {
    this.resume()
  }

  public setSpeed(speed: number) {
    this.autoplaySpeed = speed
  }

  public recalculateWidth() {
    this.calculateContentWidth()
  }
}
